"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  type ReactNode,
} from "react";

import type { ConfirmLabels, DialogService } from "./dialog-service";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./base/dialog";
import { Button } from "./button";

type DialogOptions = {
  title?: string;
  body: ReactNode;
  /**
   * Given a function, it receives the resolver, so a custom footer can settle
   * the promise with `true` rather than only the `false` a dismissal gives.
   */
  footer?: ReactNode | ((close: (value: boolean) => void) => ReactNode);
  className?: string;
};

type DialogContextType = {
  openDialog: (options: DialogOptions) => Promise<boolean>;
  alert: (message: ReactNode, title?: string) => Promise<boolean>;
  confirm: (
    message: string,
    title?: string,
    labels?: ConfirmLabels,
  ) => Promise<boolean>;
};

const DialogContext = createContext<DialogContextType | null>(null);

export function DialogProvider({ children }: { children: ReactNode }) {
  const { t } = useLingui();
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<DialogOptions | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const openDialog = useCallback((options: DialogOptions) => {
    return new Promise<boolean>((resolve) => {
      // A dialog opened over another one replaces it, and the caller waiting on
      // the one being replaced never hears from it otherwise. Settling it as a
      // dismissal is what the user sees happen: their choice was never taken.
      resolverRef.current?.(false);
      setConfig(options);
      resolverRef.current = resolve;
      setIsOpen(true);
    });
  }, []);

  const close = useCallback((value: boolean) => {
    setIsOpen(false);
    if (resolverRef.current) {
      resolverRef.current(value);
      resolverRef.current = null;
    }
  }, []);

  const alert = useCallback(
    (message: ReactNode, title = t`Notice`) =>
      openDialog({
        title,
        body: <div className="text-sm text-muted-foreground">{message}</div>,
        footer: (
          <Button onClick={() => close(true)}>
            <Trans>OK</Trans>
          </Button>
        ),
      }),
    [openDialog, close, t],
  );

  const confirm = useCallback(
    (message: string, title = t`Confirm`, labels: ConfirmLabels = {}) =>
      openDialog({
        title,
        body: <div className="text-sm text-muted-foreground">{message}</div>,
        footer: (
          <div className="flex justify-end gap-2">
            <Button onClick={() => close(false)}>
              {labels.cancelLabel ?? t`Cancel`}
            </Button>
            <Button onClick={() => close(true)}>
              {labels.confirmLabel ?? t`Confirm`}
            </Button>
          </div>
        ),
      }),
    [openDialog, close, t],
  );

  // Set global service on mount, clean up on unmount
  useEffect(() => {
    window.dialogService = { alert, confirm } satisfies DialogService;

    return () => {
      delete window.dialogService;
    };
  }, [alert, confirm]);

  return (
    <DialogContext.Provider value={{ openDialog, alert, confirm }}>
      {children}

      <Dialog open={isOpen} onOpenChange={(open) => !open && close(false)}>
        <DialogContent className={config?.className} showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>{config?.title || t`Notification`}</DialogTitle>
          </DialogHeader>

          <div className="py-4">{config?.body}</div>

          {config?.footer && (
            <DialogFooter>
              {typeof config.footer === "function"
                ? config.footer(close)
                : config.footer}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </DialogContext.Provider>
  );
}

export const useDialog = () => {
  const context = useContext(DialogContext);
  if (!context) throw new Error("useDialog must be used within DialogProvider");
  return context;
};
